<template>
  <div class="w-full h-full flex flex-col gap-4 pb-4 overflow-y-auto">
    <!-- Status + created -->
    <div class="flex justify-between items-center">
      <Tag :severity="statusSeverity" :value="statusLabel" />
      <span class="text-sm text-gray-500">{{ formatDate(currentOrder.createdAt) }}</span>
    </div>

    <!-- Customer -->
    <div class="flex flex-col gap-2 border border-gray-200 rounded-lg p-4">
      <div class="flex justify-between items-center">
        <div class="flex flex-col">
          <span class="font-medium">{{ currentOrder.customerName }}</span>
          <span class="text-sm text-gray-500" dir="ltr">{{ currentOrder.customerPhone }}</span>
        </div>
        <div class="flex gap-2">
          <Button icon="pi pi-phone" size="small" rounded outlined @click="callCustomer" />
          <Button icon="pi pi-whatsapp" size="small" rounded outlined @click="whatsappCustomer" />
        </div>
      </div>
      <span v-if="currentOrder.customerNote" class="text-sm bg-gray-50 rounded-lg p-2">
        <i class="pi pi-comment text-xs ml-1" /> {{ currentOrder.customerNote }}
      </span>
    </div>

    <!-- Items -->
    <div class="flex flex-col gap-3">
      <span class="font-medium">מוצרים</span>
      <div v-for="item of currentOrder.items" :key="item.id" class="flex justify-between items-center text-sm">
        <span>{{ item.name }} <span class="text-gray-500 ms-1">{{ item.amount }}x</span></span>
        <span>₪{{ item.price * item.amount }}</span>
      </div>
      <div v-if="currentOrder.couponDiscount" class="flex justify-between items-center text-sm border-t pt-2">
        <span class="text-gray-500">הנחת קופון ({{ currentOrder.couponDiscount.code }})</span>
        <span class="text-green-600">₪{{ currentOrder.couponDiscount.amount }}-</span>
      </div>
      <div class="flex justify-between items-center font-medium"
           :class="currentOrder.couponDiscount ? '' : 'border-t pt-2'">
        <span>{{ awaitingCharge ? 'סה״כ לחיוב' : 'סה״כ שולם' }}</span>
        <span>₪{{ currentOrder.totalAmount }}</span>
      </div>
    </div>

    <!-- Branch -->
    <div v-if="adminStore.hasBranches" class="flex flex-col gap-1">
      <span class="text-xs text-gray-500">סניף לאיסוף</span>
      <span class="text-sm font-medium">{{ branchName }}</span>
    </div>

    <!-- Collect-card order: the customer saved a card at checkout, the business charges it -->
    <div v-if="awaitingCharge" class="flex flex-col gap-2 border border-amber-200 bg-amber-50 rounded-lg p-3">
      <span class="text-sm font-medium"><i class="pi pi-credit-card text-amber-600 ml-1" /> כרטיס אשראי שמור — טרם חויב</span>
      <span class="text-xs text-gray-600">הלקוח/ה שמר/ה כרטיס בהזמנה. ניתן לחייב עד ₪{{ currentOrder.totalAmount }}.</span>
      <Button
        :label="`חיוב הכרטיס השמור (₪${currentOrder.totalAmount})`"
        icon="pi pi-credit-card"
        size="small"
        :loading="charging"
        @click="chargeSavedCard" />
    </div>

    <!-- Timeline -->
    <div class="flex flex-col gap-1 text-sm text-gray-500">
      <span v-if="currentOrder.cardSavedAt"><i class="pi pi-credit-card ml-1" /> כרטיס נשמר: {{ formatDate(currentOrder.cardSavedAt) }}</span>
      <span v-if="currentOrder.paidAt"><i class="pi pi-check-circle text-green-500 ml-1" /> שולמה: {{ formatDate(currentOrder.paidAt) }}</span>
      <span v-if="currentOrder.readyAt"><i class="pi pi-box ml-1" /> מוכנה: {{ formatDate(currentOrder.readyAt) }}</span>
      <span v-if="currentOrder.readyNotificationSentAt"><i class="pi pi-send ml-1" /> נשלח עדכון ללקוח: {{ formatDate(currentOrder.readyNotificationSentAt) }}</span>
      <span v-if="currentOrder.pickedUpAt"><i class="pi pi-check text-green-500 ml-1" /> נאספה: {{ formatDate(currentOrder.pickedUpAt) }}</span>
      <span v-if="currentOrder.cancelledAt"><i class="pi pi-times text-red-500 ml-1" /> בוטלה: {{ formatDate(currentOrder.cancelledAt) }}</span>
    </div>

    <!-- Actions -->
    <div class="flex flex-col gap-2 mt-auto">
      <template v-if="isReceived">
        <Button
          label="ההזמנה מוכנה לאיסוף"
          icon="pi pi-box"
          :loading="updating"
          @click="markReady" />
        <!-- The customer notification (channel + content) is configured on the
             automations page, like every other automated message. -->
        <span class="text-xs text-gray-500 text-center">
          בסימון כמוכנה יישלח ללקוח עדכון אוטומטי, לפי הגדרת ההודעות האוטומטיות
        </span>
      </template>
      <Button
        v-if="isReady"
        label="ההזמנה נאספה"
        icon="pi pi-check"
        :loading="updating"
        @click="markPickedUp" />
      <Button
        v-if="isReceived || isReady"
        label="ביטול הזמנה"
        severity="danger"
        text
        :loading="updating"
        @click="cancelOrder" />
      <span v-if="isReceived || isReady" class="text-xs text-gray-400 text-center">
        ביטול ההזמנה מחזיר את המוצרים למלאי. זיכוי כספי מתבצע מול ספק הסליקה.
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { DateTime } from 'luxon';
import Tag from 'primevue/tag';
import { useToast } from 'vue-toastification';
import { useConfirm } from 'primevue/useconfirm';
import { IProductOrder, MAIN_BRANCH_ID, ProductOrderStatusEnum } from '@torim-app/types';
import { sendWhatsApp } from '@torim-app/utils';
import { useAdminStore } from '@app/stores';
import { ProductOrdersApi } from '@app/api/admin/product-orders-api';
import { PaymentsApi } from '@app/api/admin';
import { trackEvent } from '@app/composables/posthog';

const props = defineProps<{
  order: IProductOrder;
  onUpdated?: () => Promise<void> | void;
  onClose?: () => void;
}>();

const adminStore = useAdminStore();
const toast = useToast();
const confirm = useConfirm();

const currentOrder = ref<IProductOrder>({ ...props.order });
const updating = ref(false);
const charging = ref(false);

// Placed on a saved card and not charged yet (collect-card business)
const awaitingCharge = computed(() =>
  !!currentOrder.value.cardSavedAt && !currentOrder.value.paidAt
  && !!currentOrder.value.paymentRequestUuid
  && currentOrder.value.status !== ProductOrderStatusEnum.Cancelled);

const chargeSavedCard = () => {
  confirm.require({
    header: 'חיוב הכרטיס השמור',
    message: `לחייב את הכרטיס של ${currentOrder.value.customerName} ב-₪${currentOrder.value.totalAmount}?`,
    acceptLabel: 'חיוב',
    rejectLabel: 'ביטול',
    accept: async () => {
      charging.value = true;
      try {
        trackEvent('product_order_saved_card_charged', { total_amount: currentOrder.value.totalAmount });
        const result = await PaymentsApi.chargeSavedCard({
          paymentRequestUuid: currentOrder.value.paymentRequestUuid!,
          amount: currentOrder.value.totalAmount
        });
        if (!result.success) {
          toast.error(result.error ?? 'החיוב נכשל');
          return;
        }
        currentOrder.value = { ...currentOrder.value, paidAt: new Date().toISOString() };
        toast.success('הכרטיס חויב בהצלחה');
        await props.onUpdated?.();
      } catch (e: any) {
        toast.error(e?.error ?? e?.message ?? 'החיוב נכשל');
      } finally {
        charging.value = false;
      }
    }
  });
};

const isReceived = computed(() => currentOrder.value.status === ProductOrderStatusEnum.Received);
const isReady = computed(() => currentOrder.value.status === ProductOrderStatusEnum.Ready);

const branchName = computed(() =>
  adminStore.getBranchSelectOptions.find(b => b.id === (currentOrder.value.branchId ?? MAIN_BRANCH_ID))?.name ?? '');

const statusLabel = computed(() => {
  switch (currentOrder.value.status) {
    case ProductOrderStatusEnum.Received: return 'חדשה';
    case ProductOrderStatusEnum.Ready: return 'מוכנה לאיסוף';
    case ProductOrderStatusEnum.PickedUp: return 'נאספה';
    case ProductOrderStatusEnum.Cancelled: return 'בוטלה';
    default: return currentOrder.value.status;
  }
});

const statusSeverity = computed(() => {
  switch (currentOrder.value.status) {
    case ProductOrderStatusEnum.Received: return 'info';
    case ProductOrderStatusEnum.Ready: return 'warn';
    case ProductOrderStatusEnum.PickedUp: return 'success';
    case ProductOrderStatusEnum.Cancelled: return 'danger';
    default: return 'secondary';
  }
});

const formatDate = (date?: Date | string) =>
  date ? DateTime.fromISO(String(date)).setLocale('he').toLocaleString(DateTime.DATETIME_MED) : '';

const callCustomer = () => {
  window.open(`tel:${currentOrder.value.customerPhone}`, '_self');
};

const whatsappCustomer = () => {
  sendWhatsApp(currentOrder.value.customerPhone);
};

const updateStatus = async (status: ProductOrderStatusEnum, successMessage: string) => {
  updating.value = true;
  try {
    currentOrder.value = await ProductOrdersApi.updateStatus(currentOrder.value._id!, status);
    toast.success(successMessage);
    await props.onUpdated?.();
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לעדכן את ההזמנה, אנא נסו שוב');
  } finally {
    updating.value = false;
  }
};

const markReady = async () => {
  trackEvent('product_order_marked_ready');
  await updateStatus(ProductOrderStatusEnum.Ready, 'ההזמנה סומנה כמוכנה לאיסוף');
};

const markPickedUp = async () => {
  trackEvent('product_order_picked_up');
  await updateStatus(ProductOrderStatusEnum.PickedUp, 'ההזמנה סומנה כנאספה');
  props.onClose?.();
};

const cancelOrder = () => {
  confirm.require({
    message: 'לבטל את ההזמנה? המוצרים יוחזרו למלאי.',
    header: 'ביטול הזמנה',
    acceptLabel: 'ביטול ההזמנה',
    rejectLabel: 'חזרה',
    acceptClass: 'p-button-danger',
    accept: async () => {
      trackEvent('product_order_cancelled');
      await updateStatus(ProductOrderStatusEnum.Cancelled, 'ההזמנה בוטלה');
      props.onClose?.();
    }
  });
};
</script>

<style scoped lang="scss">
</style>
